/**
 * Renders a ticker as "AWAY @ HOME — SPORT — MM-DD" plus the team the user
 * actually backed given their YES/NO side. Falls back to a truncated raw
 * ticker for non-game markets.
 */

import { parseTicker } from './parse';
import { kalshiTotalLineFromStrike } from './totalLine';
import { isNflExtendedSeriesTicker, nflPropBetLabel, nflPropBetParts } from './sports/nfl';
import { useMarketStore } from '@/stores/marketStore';
import { retainedIdentity, savedHistoricalIdentity } from '@/stores/accountMarketIdentity';
import type { HydraMarket } from '@/clients/HydraClient';
import { MULTI_OUTCOME_SPORTS, type Sport } from '@/views/ValueDashboard/types';
import {
  UFC_FIGHT_PROP_FAMILY_LABEL,
  parseUfcFightPropTicker,
  resolveUfcFightPropLeg,
  ufcEventDate,
  ufcEventPair,
  ufcFightPropBadge,
  ufcPairSplits,
  type UfcFightPropLeg,
} from '@/lib/ufc/fightProps';
import { FIGHT_PROP_GROUP_LABEL, classifyPolyFightProp, type PolyFightProp } from './polyFightProps';
import type { GameInfo, ParsedTicker, TickerCategory } from './types';

export interface MarketDescription {
  marketLabel: string;
  /** The market's named (YES) side, as a CODE ("BAL", "NYC-2.5", "Draw").
   *  Rendered standalone as the "(BAL) NO" side badge on cards and across the
   *  Positions / Orders / Trader / Org tables, so it stays short — except an
   *  NFL ladder prop's, which names its stat; those badges show `badgeTeam`
   *  for it instead (#964; see `marketType`). */
  betTeam: string | null;
  /** The token a card highlights INSIDE `marketLabel` (`BetTeamTitle` →
   *  `findTeamToken`). Normally identical to `betTeam` — but for a sport whose
   *  matchup is spelled with feed-supplied club names, the label says "New York
   *  City" where `betTeam` says "NYC", and a whole-token search for the code
   *  would find nothing and silently drop the highlight. Null when the label
   *  contains no token to highlight (a draw, a line bet). */
  titleTeam: string | null;
  /** #964 — what a ladder prop's threshold counts ("Receiving Yards"), when
   *  `betTeam` ends with it. Present only for NFL ladder props; text surfaces
   *  print `betTeam` whole, while a one-line side badge — which may ellipsize
   *  its label (#603) — shows `badgeTeam` with this beside or under it. */
  marketType?: string | null;
  /** The side badge's label when `marketType` is set: `betTeam` without it
   *  ("K. Johnson #26 15+"). Absent otherwise — the badge shows `betTeam`. */
  badgeTeam?: string | null;
  /** Exact parsed ladder rung, retained when a narrow badge shortens the player. */
  badgeThreshold?: string;
}

/** Kalshi's soccer 1X2 subtitles are scope-qualified — the live final's ESP
 *  leg reads `"Reg Time: Spain"`, not `"Spain"` (verified against prod hydra
 *  2026-07-18). Borrowing that name for the cup-winner leg would mislabel it
 *  with a scope that is the OPPOSITE of what the winner market settles on
 *  (the Cup includes extra time + penalties; "Reg Time" excludes them), so
 *  the qualifier is dropped. Returns null when nothing readable survives —
 *  a bare code is the caller's next fallback anyway. */
function stripOutcomeQualifier(name: string, code: string): string | null {
  const cleaned = (name.includes(': ') ? name.slice(name.lastIndexOf(': ') + 2) : name).trim();
  return cleaned && cleaned.toUpperCase() !== code ? cleaned : null;
}

/** The display name the linked game row gives this leg's outcome.
 *
 *  Advance-stamped legs carry the related game's canonical key plus the
 *  canonical `yesOutcome` code but no away/home codes of their own (#98
 *  shared contract), so the row is found by key and the side is picked by
 *  matching that code. Returns null whenever the relationship is absent or
 *  the row isn't ingested — the caller then degrades down its chain. */
function linkedOutcomeName(mkt: HydraMarket | null | undefined): string | null {
  const key = mkt?.canonicalGameKeyV2 ?? null;
  const code = mkt?.yesOutcome?.trim().toUpperCase();
  if (!key || !code || !mkt?.sport) return null;
  const sport = mkt.sport.toLowerCase() as Sport;
  const store = useMarketStore.getState();
  // Multi-outcome sports (soccer — the only series that has these legs) carry
  // the readable name on the per-outcome entry; the event's own away/homeName
  // are bare codes. Binary sports keep the name on the row itself.
  if (MULTI_OUTCOME_SPORTS.has(sport)) {
    const event = store
      .getMultiOutcomeEvents(sport, { dateFilter: 'all', statusFilter: 'all' })
      .find((e) => e.key === key);
    const name = event?.outcomes.find((o) => o.code.toUpperCase() === code)?.name;
    return name ? stripOutcomeQualifier(name, code) : null;
  }
  const row = store
    .getGames(sport, 'moneyline', { dateFilter: 'all', statusFilter: 'all' })
    .find((r) => r.key === key);
  if (!row) return null;
  if (row.awayCode.toUpperCase() === code) return row.awayName || null;
  if (row.homeCode.toUpperCase() === code) return row.homeName || null;
  return null;
}

/** Both competitors of a 1v1 (tennis / UFC / boxing) market, in pack order
 *  (player1 first), each as hydra names it (`canonicalTeamNames`) or as its
 *  canonical surname code — plus this market's own YES competitor.
 *
 *  The market's YES side is its `canonicalYesTeam`, one of the game key's two
 *  codes; the other code is the key's other one, so no sibling ticker is
 *  rebuilt and no subtitle is read. Outcome-anchored to the pack so the order
 *  matches the ticker's.
 *
 *  MUST STAY O(1). describeMarket runs during render for every position, order,
 *  fill and card on the dashboard. An earlier revision resolved the pair by
 *  scanning `getGames(sport, …)`, whose result cache is invalidated on every
 *  store version bump — i.e. every feed flush — so each row re-derived a whole
 *  sport's game universe, synchronously, several times a second. That shipped
 *  to staging on 2026-07-31 and made the dashboard crawl and render
 *  incomplete. Never reach for a selector here.
 *
 *  A market hydra has NOT KEYED (no `canonicalGameKeyV2`: ESPORTS, CRICKET
 *  and CBB are outside hydracore's identity set) keeps the names it showed
 *  before canonical-team-names: its own subtitle beside its sibling's, the
 *  sibling found by rebuilding `{eventTicker}-{otherCode}` — one O(1) lookup.
 *  Bridge, 2026-10-21; a keyed market never reads a subtitle.
 *
 *  Degrades to hydra's full-surname `canonicalTeams` (codes), then to null
 *  (caller falls back to ticker fragments). */
function versusNames(
  mkt: HydraMarket | null | undefined,
  versus: { player1: { playerCode: string }; player2: { playerCode: string } },
  outcome: string | null,
): { a: string; b: string; yes: string | null } | null {
  if (!mkt) return null;
  const store = useMarketStore.getState();
  const pair = gameKeyPair(mkt);
  const yes = mkt.canonicalYesTeam?.trim().toUpperCase();
  if (pair && yes && pair.includes(yes)) {
    const other = pair[0] === yes ? pair[1] : pair[0];
    const own = store.getTeamName(mkt.ticker, yes) ?? mkt.canonicalYesTeam!.trim();
    const otherName = store.getTeamName(mkt.ticker, other) ?? canonicalSpelling(mkt, other);
    if (outcome === versus.player1.playerCode) return { a: own, b: otherName, yes: own };
    if (outcome === versus.player2.playerCode) return { a: otherName, b: own, yes: own };
  }
  if (!mkt.canonicalGameKeyV2) {
    const unkeyed = unkeyedVersusNames(mkt, versus, outcome);
    if (unkeyed) return unkeyed;
  }
  const teams = mkt.canonicalTeams;
  return teams ? { a: teams[0], b: teams[1], yes: null } : null;
}

/** An unkeyed 1v1 market's two subtitles in pack order, or null when either
 *  is missing (see versusNames). */
function unkeyedVersusNames(
  mkt: HydraMarket,
  versus: { player1: { playerCode: string }; player2: { playerCode: string } },
  outcome: string | null,
): { a: string; b: string; yes: string } | null {
  const own = mkt.subtitle?.trim();
  const c1 = versus.player1.playerCode;
  const c2 = versus.player2.playerCode;
  const otherCode = outcome === c1 ? c2 : outcome === c2 ? c1 : null;
  const eventTicker = mkt.eventTicker || mkt.ticker.replace(/-[^-]+$/, '');
  if (!own || !otherCode || !eventTicker) return null;
  const other = useMarketStore.getState().getMarket(`${eventTicker}-${otherCode}`)?.subtitle?.trim();
  if (!other) return null;
  return outcome === c1 ? { a: own, b: other, yes: own } : { a: other, b: own, yes: own };
}

/** The two codes of a frame's canonical game key, upper-cased, or null. */
function gameKeyPair(mkt: HydraMarket): [string, string] | null {
  const parts = (mkt.canonicalGameKeyV2 || mkt.canonicalGameKey || '').split('|');
  const [a, b] = parts.slice(-2);
  return parts.length >= 4 && a && b ? [a.toUpperCase(), b.toUpperCase()] : null;
}

/** A key code as hydra spells it in `canonicalTeams` ("Etcheverry", not the
 *  key's upper-cased "ETCHEVERRY"), else as the key does. */
function canonicalSpelling(mkt: HydraMarket, code: string): string {
  return mkt.canonicalTeams?.find((t) => t.toUpperCase() === code) ?? code;
}

/** Pull the canonical line out of Kalshi's `yes_sub_title` (hydra forwards
 *  it as `subtitle`). Same regex works for spread ("...wins by over 2.5
 *  goals") and total ("Over 203.5 points scored") subtitles. */
function lineFromSubtitle(subtitle: string | null | undefined): number | null {
  if (!subtitle) return null;
  const m = subtitle.match(/\b(?:over|under) (\d+(?:\.\d+)?)\b/i);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

/** Human-readable description for the CFB families added by #729.
 *
 * These series deliberately do not enter the legacy ticker parser: college
 * team codes are variable-width, and packed outcomes such as COLOGT/GTGT are
 * not safely splittable. The live frame already carries every identity field
 * the card needs, so resolve in O(1) from that frame and fail back to the raw
 * ticker unless BOTH the canonical matchup and Kalshi's outcome wording are
 * present. Full-game GAME/SPREAD/TOTAL stay on their established formatter. */
function describeExtendedCfbMarket(
  ticker: string,
  mkt: HydraMarket | null | undefined,
): MarketDescription | null {
  if (!mkt) return null;
  const sport = mkt.sport?.toUpperCase();
  const isNfl = sport === 'NFL';
  if (sport !== 'CFB' && sport !== 'NCAAF' && !isNfl) return null;
  if (mkt.marketType === 'future') return null;

  const series = (mkt.seriesTicker || ticker.split('-')[0] || '').toUpperCase();
  if (series === 'KXNCAAFGAME' || series === 'KXNCAAFSPREAD' || series === 'KXNCAAFTOTAL') {
    return null;
  }
  // NFL (nfl-game-lines-props): the full-game lines, the period lines and
  // the player props all have their own parsers; only the game-event
  // families (OT, 1H/FT, TOTALTD, 2PTCONV, TEAMFIRSTTD, TEAMTOTAL, EQBTTS,
  // quarter BTTS, RACE, BOTH) read off the wire here.
  if (isNfl && (
    series === 'KXNFLGAME' || series === 'KXNFLSPREAD' || series === 'KXNFLTOTAL'
    || /^KXNFL(?:1H|2H|[1-4]Q)(?:SPREAD|TOTAL)?$/.test(series)
    || isNflExtendedSeriesTicker(series)
  )) {
    return null;
  }

  const away = mkt.canonicalAwayCode?.trim().toUpperCase();
  const home = mkt.canonicalHomeCode?.trim().toUpperCase();
  const outcome = mkt.subtitle?.trim();
  if (!away || !home || !outcome) return null;

  // Kalshi's title names the family; the subtitle names THIS outcome. Keeping
  // both makes a card distinguish "Overtime: 1+ overtime periods" from every
  // other market under the same game without treating the series title as an
  // outcome (the DNL-003 failure mode).
  const rawTitle = mkt.title?.trim() ?? '';
  const colon = rawTitle.indexOf(':');
  const family = colon >= 0 ? rawTitle.slice(colon + 1).trim() : '';
  const descriptor = family && family.toLowerCase() !== outcome.toLowerCase()
    ? `${family}: ${outcome}`
    : outcome;

  const canonicalKey = mkt.canonicalGameKeyV2 || mkt.canonicalGameKey || '';
  const canonicalDate = canonicalKey.split('|')[1] || null;
  const date = canonicalDate && /^\d{4}-\d{2}-\d{2}$/.test(canonicalDate)
    ? formatGameDate(canonicalDate)
    : null;
  // canonicalTeamNames, one O(1) read per side; the code when hydra has none.
  const store = useMarketStore.getState();
  const awayLabel = store.getTeamName(ticker, away) ?? away;
  const homeLabel = store.getTeamName(ticker, home) ?? home;
  const marketLabel = [
    `${awayLabel} @ ${homeLabel}`,
    descriptor,
    isNfl ? 'NFL' : 'CFB',
    date,
  ].filter((s): s is string => !!s).join(' — ');

  const yesTeam = mkt.canonicalYesTeam?.trim().toUpperCase() ?? null;
  const titleTeam = yesTeam === away ? awayLabel : yesTeam === home ? homeLabel : null;
  return { marketLabel, betTeam: outcome, titleTeam };
}

/** Spread outcome `TEAM<N>` → "TEAM-X.5". Prefers the line from hydra's
 *  forwarded `subtitle`; falls back to `title` (spreads only, for
 *  pre-subtitle frames) then `bucket − 0.5` math. Total outcome `<N>`
 *  → "TOT X.5" via the same subtitle path with per-sport strike-math
 *  fallback. Moneyline / unknown → raw outcome. */
function deriveBetTeam(
  ticker: string,
  category: TickerCategory,
  outcome: string | null,
  fallback: string,
): string {
  if (!outcome) return fallback;
  if (category.endsWith('-spread')) {
    const m = outcome.match(/^([A-Z]+)(\d+)$/);
    if (m) {
      const mkt = useMarketStore.getState().getMarket(ticker);
      const line =
        mkt?.floorStrike ??
        lineFromSubtitle(mkt?.subtitle) ??
        lineFromSubtitle(mkt?.title) ??
        Number(m[2]) - 0.5;
      return `${m[1]}-${line}`;
    }
  }
  if (category.endsWith('-total')) {
    const m = outcome.match(/^(\d+)$/);
    if (m) {
      const mkt = useMarketStore.getState().getMarket(ticker);
      const strike = Number(m[1]);
      // Live metadata wins. Full-game ticker-only fallback shares the dated
      // venue convention with the ledger, including settled historical rows.
      const line =
        mkt?.floorStrike ??
        lineFromSubtitle(mkt?.subtitle) ??
        kalshiTotalLineFromStrike(ticker, strike);
      return `TOT ${line}`;
    }
  }
  return outcome;
}

function isSoccerDraw(outcome: string): boolean {
  return outcome === 'TIE' || outcome === 'DRAW';
}

/** Categories whose parser deliberately declines to split a packed competitor
 *  pair, leaving `game` null rather than midpoint-guessing it: CFB lines
 *  (#532 — a total's outcome is the strike bucket, no team token at all) and
 *  soccer / esports draw-tie-BTTS legs (#837 — an outcome that names no
 *  competitor). Every one of them recovers, when it can, from producer-owned
 *  canonical identity. A category NOT listed here that parses without a game
 *  has a parse gap, and its own fallback, and must not be handed a matchup
 *  from somewhere else. */
const UNANCHORED_PAIR_CATEGORIES: ReadonlySet<TickerCategory> = new Set<TickerCategory>([
  'cfb-spread',
  'cfb-total',
  'soccer-game',
  'esports-game',
]);

// Esports currently lacks producer identity on sampled frames. Keep the
// fallback unknown there, but accept the same exact-frame contract when the
// producer supplies it; rollout timing must not require another display fix.

/** The producer's own away/home codes for this exact ticker, or null.
 *
 *  Up to three O(1) map reads, the first two already made elsewhere in this
 *  file: the
 *  matchup index first (soccer) — it carries hydra's canonical orientation and
 *  resolves for EVERY market family stamped with the fixture's canonical key
 *  (1X2, draw, spread, total, BTTS, To Advance) — then the frame's own
 *  canonical fields — then, only when the store holds no live row at all,
 *  #1134's identity kept for the account's own tickers. NEVER a selector; see
 *  this file's MUST-STAY-O(1) note.
 *
 *  A missing answer is a missing answer. Soccer is stamped reliably (22/22
 *  draw legs sampled on hc1, 2026-09-09); esports is stamped not at all, and
 *  the caller renders the ticker and the bet's meaning rather than inventing a
 *  pair to fill the gap. */
function canonicalPairFor(
  ticker: string,
  category: TickerCategory,
): { awayCode: string; homeCode: string } | null {
  const store = useMarketStore.getState();
  // The matchup index is the better source for soccer because it aggregates
  // the fixture's frames under one canonical key: a draw leg whose own frame
  // arrived without canonical codes still resolves from a sibling's. This is
  // ORIENTATION, and its scope is unchanged (#837): every other category reads
  // the frame's own canonical pair, as it always has.
  const named = category === 'soccer-game' ? store.getGameMatchup(ticker) : null;
  if (named?.awayCode && named.homeCode) {
    return {
      awayCode: named.awayCode.toUpperCase(),
      homeCode: named.homeCode.toUpperCase(),
    };
  }
  const mkt = store.getMarket(ticker);
  if (mkt?.canonicalAwayCode && mkt.canonicalHomeCode) {
    return {
      awayCode: mkt.canonicalAwayCode.toUpperCase(),
      homeCode: mkt.canonicalHomeCode.toUpperCase(),
    };
  }
  // #1134: the same producer identity, kept for this account's own tickers,
  // and read only when the store holds NO live row (retired, or not yet back
  // after a reload). A live row without a pair is the producer saying the pair
  // is unknown or contradicts the ticker, and that answer wins.
  if (!mkt) {
    const kept = retainedIdentity(ticker);
    if (kept) return { awayCode: kept.awayCode, homeCode: kept.homeCode };
  }
  return null;
}

/** The away/home pair to DISPLAY for a ticker: the parser's own split, or the
 *  producer's canonical identity where the parser deliberately declined to
 *  guess. One rule, exported, because three surfaces have to agree on it — the
 *  card label here, the Value Dashboard's held-position retention keys, and
 *  MyTrades' Game Detail link. They drifted apart once already: each derived
 *  the pair from `parseTicker(...).game` alone, so when #837 stopped that
 *  returning a midpoint guess, two of them silently lost their answer.
 *
 *  Two families give canonical identity DIFFERENT authority, deliberately:
 *
 *    • CFB lines: canonical WINS outright (#532). College codes are
 *      variable-width, so even a spread the parser managed to anchor has only
 *      pack order to orient it, and Hydra verifies Kalshi's event-level pair
 *      before stamping.
 *    • Soccer: canonical is the FALLBACK. An anchored leg keeps the orientation
 *      it has always had, so every ordinary two-team label stays byte-identical
 *      and #837 changes only the legs that used to guess.
 *
 *  Every other category resolves to `parsed.game` exactly as before — a
 *  category that parses without a game and is not listed above has a parse gap,
 *  not an unanchorable pack, and must not be handed a matchup from elsewhere.
 *
 *  O(1): at most three map reads (the third, #1134's kept identity, only when
 *  no live row exists), and none at all when the parser resolved the pair
 *  itself. Never a selector — see this file's MUST-STAY-O(1) note. */
export function resolvedGamePair(ticker: string, parsed: ParsedTicker): GameInfo | null {
  if (savedHistoricalIdentity(ticker)?.pairConflicted && !useMarketStore.getState().getMarket(ticker)
      && (pairComesFromProducer(ticker))) return null;
  const canonicalWins = parsed.category === 'cfb-spread' || parsed.category === 'cfb-total';
  if (canonicalWins) return canonicalPairFor(ticker, parsed.category) ?? parsed.game;
  if (parsed.game) return parsed.game;
  return UNANCHORED_PAIR_CATEGORIES.has(parsed.category)
    ? canonicalPairFor(ticker, parsed.category)
    : null;
}

/** #1134: whether this ticker's display pair can come from producer identity
 *  under `resolvedGamePair`'s rule — CFB lines always, an unanchored leg when
 *  the parser declined. Only these labels ever read a kept pair, so only these
 *  tickers are worth keeping one for. Keep the two in lockstep. */
export function pairComesFromProducer(ticker: string): boolean {
  const parsed = parseTicker(ticker);
  if (parsed.category === 'cfb-spread' || parsed.category === 'cfb-total') return true;
  return !parsed.game && UNANCHORED_PAIR_CATEGORIES.has(parsed.category);
}

/** What an unresolvable leg still says it is — "Draw", "Tie", "Both Score".
 *
 *  The matchup may be unknown, but the BET never is: these outcomes name the
 *  wager directly instead of naming a competitor, which is exactly why they
 *  cannot anchor the pack. Null for a leg that DOES name a competitor, because
 *  a missing matchup there is a parse gap rather than an unanchorable pack, and
 *  the raw-ticker fallback (with its diagnostic) is the right answer for it. */
function unresolvedBetMeaning(parsed: ParsedTicker): string | null {
  if (parsed.outcome === 'BTTS') return 'Both Score';
  if (!parsed.outcome || !isSoccerDraw(parsed.outcome)) return null;
  return parsed.category === 'esports-game' ? 'Tie' : 'Draw';
}

/** #848 — a Kalshi UFC fight prop (KXUFCROUNDS / MOV / MOF / VICROUND /
 *  DISTANCE): "Brendan Allen vs Christian Duncan — UFC Method of victory —
 *  10-10" with a leg badge ("DUN by Submission"). The ticker parser has no
 *  game for these series, so without this every leg of a bout fell through
 *  to the same truncated tail, and the two draw legs ("…ALLDUN-DRAW" under
 *  MOV and MOF) read identically, cancel confirmation included.
 *
 *  Identity is the ticker's own closed grammar (`lib/ufc/fightProps`), never
 *  a title. The packed pair is cut only where BOTH of the bout's KXUFCFIGHT
 *  legs are in the store (each leg's YES fighter, named by
 *  `canonicalTeamNames`), or, for a sided leg, where exactly one cut lets the
 *  suffix name a fighter; failing both, the producer's canonical away/home
 *  surnames, then the raw pair.
 *  At most `pair.length` O(1) map reads; never a selector (see versusNames).
 *  Null when the ticker is not a fight prop or its leg does not parse — the
 *  caller's existing fallback then applies unchanged. */
function describeUfcFightProp(ticker: string): MarketDescription | null {
  const t = parseUfcFightPropTicker(ticker);
  if (!t) return null;
  const store = useMarketStore.getState();
  const pair = ufcEventPair(t.eventCode);

  let codes: [string, string] | null = null;
  let names: [string, string] | null = null;
  for (const [a, b] of ufcPairSplits(pair)) {
    const fa = store.getMarket(`KXUFCFIGHT-${t.eventCode}-${a}`);
    const fb = fa ? store.getMarket(`KXUFCFIGHT-${t.eventCode}-${b}`) : undefined;
    if (!fa || !fb) continue;
    if (codes) { codes = null; names = null; break; } // two live cuts: ambiguous
    codes = [a, b];
    names = [fightLegName(fa) ?? a, fightLegName(fb) ?? b];
  }

  let leg = resolveUfcFightPropLeg(t, codes ?? []);
  if (!codes) {
    // Not in the store (historical row, feed not loaded): a sided suffix can
    // still anchor the cut by itself when exactly one cut admits it.
    const hits = new Map<string, { split: [string, string]; leg: UfcFightPropLeg }>();
    for (const split of ufcPairSplits(pair)) {
      const l = resolveUfcFightPropLeg(t, split);
      if (l && 'fighterCode' in l && l.fighterCode) hits.set(split.join('|'), { split, leg: l });
    }
    if (hits.size === 1) {
      const [hit] = hits.values();
      codes = hit!.split;
      leg = hit!.leg;
    }
  }
  if (!leg) return null;

  const mkt = store.getMarket(ticker);
  const canonAway = mkt?.canonicalAwayCode?.trim();
  const canonHome = mkt?.canonicalHomeCode?.trim();
  const shown: [string, string] | null =
    names ?? codes ?? (canonAway && canonHome ? [canonAway, canonHome] : null);
  const matchup = shown ? `${shown[0]} vs ${shown[1]}` : pair;
  const date = ufcEventDate(t.eventCode);
  const marketLabel = [
    matchup,
    `UFC ${UFC_FIGHT_PROP_FAMILY_LABEL[leg.family]}`,
    date ? formatGameDate(date) : null,
  ].filter((s): s is string => !!s).join(' — ');

  // Highlight the leg's own fighter where the label spells them.
  const fighterCode = 'fighterCode' in leg ? leg.fighterCode : null;
  const idx = fighterCode && codes ? codes.indexOf(fighterCode) : -1;
  const titleTeam = idx >= 0 && shown ? shown[idx]! : null;
  return { marketLabel, betTeam: ufcFightPropBadge(leg), titleTeam };
}

/** A KXUFCFIGHT leg's YES fighter as hydra names it (`canonicalTeamNames`
 *  under the leg's `canonicalYesTeam`), else that canonical surname code. */
function fightLegName(leg: HydraMarket): string | null {
  const yes = leg.canonicalYesTeam?.trim();
  if (!yes) return null;
  return useMarketStore.getState().getTeamName(leg.ticker, yes) ?? yes;
}

// One-shot diagnostic: log each distinct ticker that falls through to the
// truncated raw display so we can see what's not parsing. Remove once
// MLB total + other holdouts are accounted for.
const _loggedFallback = new Set<string>();

export function describeMarket(ticker: string, _side: 'yes' | 'no'): MarketDescription {
  const base = describeCurrentOrParsedMarket(ticker, _side);
  const saved = savedHistoricalIdentity(ticker);
  // Live unknown wins, and working specialized/parser labels stay intact.
  // Historical display cannot teach resolvedGamePair a new navigation key.
  if (!saved || useMarketStore.getState().getMarket(ticker)
      || (!base.marketLabel.startsWith('...') && !saved.pairConflicted)
      || !pairComesFromProducer(ticker)) return base;
  const parsed = parseTicker(ticker);
  const paired = saved.awayCode && saved.homeCode
    ? `${saved.awayCode} @ ${saved.homeCode}` : null;
  const date = parsed.date ? formatGameDate(parsed.date) : null;
  return {
    ...base,
    marketLabel: [paired ?? saved.description, paired ? sportFromCategory(parsed.category) : null, date]
      .filter((s): s is string => !!s).join(' — '),
    // Preserve the existing native line badge. A long literal proposition
    // cannot balloon a fills row or change this closed ticker's wager meaning.
    betTeam: deriveBetTeam(ticker, parsed.category, parsed.outcome, saved.homeCode ?? '') ?? base.betTeam,
    titleTeam: null,
  };
}

const POLY_FIGHT_METHOD_LABEL: Readonly<Record<string, string>> = {
  'ko-tko': 'KO/TKO',
  submission: 'Submission',
  decision: 'Decision',
};

/** Short YES badge for a Polymarket fight prop, from its own slug grammar
 *  (lib/kalshiTicker/polyFightProps): "Goes the distance", "O 2.5 rounds",
 *  "ALLEN by KO/TKO", "Ends before R3", "DUNCAN in R5". */
function polyFightPropBadge(ticker: string, prop: PolyFightProp): string {
  const fighter = prop.fighter ? prop.fighter.replace(/-/g, ' ').toUpperCase() : null;
  const suffix = ticker.slice(prop.stem.length + 1);
  switch (prop.group) {
    case 'distance':
      return 'Goes the distance';
    case 'rounds':
      return `O ${prop.line} rounds`;
    case 'method': {
      const method = POLY_FIGHT_METHOD_LABEL[/win-by-(ko-tko|submission|decision)$/.exec(suffix)?.[1] ?? ''] ?? 'method';
      return fighter ? `${fighter} by ${method}` : `By ${method}`;
    }
    case 'round':
      if (suffix === 'decision-or-draw') return 'Decision or draw';
      if (prop.round == null) return suffix;
      return fighter ? `${fighter} in R${prop.round}` : `Ends before R${prop.round}`;
  }
}

function describeCurrentOrParsedMarket(ticker: string, _side: 'yes' | 'no'): MarketDescription {
  const parsed = parseTicker(ticker);

  // Polymarket branch — venue-specific formatting that doesn't try to use
  // kalshi's spread/total subtitle-line derivation (poly markets don't
  // carry kalshi-shaped subtitles; the polyLine lives elsewhere).
  if (parsed.category === 'polymarket') {
    if (parsed.game) {
      const sport = parsed.series.startsWith('PM-')
        ? `Poly ${parsed.series.slice(3)}`
        : 'Poly';
      const date = parsed.date ? formatGameDate(parsed.date) : null;
      // canonicalTeamNames for the slug's codes, when hydra keyed and named
      // the game; the slug codes otherwise. One O(1) read per side.
      const { awayCode, homeCode } = parsed.game;
      const store = useMarketStore.getState();
      const awayLabel = store.getTeamName(ticker, awayCode) ?? awayCode;
      const homeLabel = store.getTeamName(ticker, homeCode) ?? homeCode;
      // A fight prop rides its fight's slug stem, so the parse above reads it
      // as the fight itself. Name the prop's own proposition instead, never
      // either fighter winning.
      const fightProp = classifyPolyFightProp(ticker);
      if (fightProp) {
        const segments = [
          `${awayLabel} @ ${homeLabel}`,
          `${sport} ${FIGHT_PROP_GROUP_LABEL[fightProp.group]}`,
          date,
        ].filter((s): s is string => !!s);
        return { marketLabel: segments.join(' — '), betTeam: polyFightPropBadge(ticker, fightProp), titleTeam: null };
      }
      const segments = [
        `${awayLabel} @ ${homeLabel}`,
        sport,
        date,
      ].filter((s): s is string => !!s);
      // A fight is one binary whose YES is the slug-first fighter (Gamma
      // outcomes[0]): the away slot here, not the home one.
      const isFight = parsed.series === 'PM-UFC' || parsed.series === 'PM-BOXING';
      const betTeam =
        parsed.outcome === 'DRAW'
          ? 'Draw'
          : parsed.outcome ?? (isFight ? awayCode : homeCode);
      // The highlight is the token the label spells for the bet's side.
      const titleTeam = betTeam === awayCode ? awayLabel : betTeam === homeCode ? homeLabel : betTeam;
      return { marketLabel: segments.join(' — '), betTeam, titleTeam };
    }
    return { marketLabel: parsed.fullLabel, betTeam: null, titleTeam: null };
  }

  // #848 — UFC fight props. Before the 1v1 branch and the raw fallback; the
  // prefix gate keeps every other ticker off the parse.
  if (ticker.startsWith('KXUFC')) {
    const fightProp = describeUfcFightProp(ticker);
    if (fightProp) return fightProp;
  }

  // Player-vs-player sports (tennis, UFC, boxing) carry no GameInfo. Render the
  // two competitors as a matchup ("Djokovic vs Sinner — Tennis — MM-DD")
  // instead of falling through to the truncated raw ticker.
  //
  // Names resolve off the live frame, never off the ticker: hydra's
  // `canonicalTeamNames` (the complete athlete names), else its full-surname
  // codes; the ticker pack ("DJO"/"SINNER", and on a fallback-keyed fight the
  // lossy first-3) is last.
  //
  // betTeam is the market's named (YES) competitor, degrading to the trailing
  // outcome code before the frame loads.
  const versus = parsed.tennis ?? parsed.fight;
  if (!parsed.game && versus) {
    const mkt = useMarketStore.getState().getMarket(ticker);
    const names = versusNames(mkt, versus, parsed.outcome);
    const sport = sportFromCategory(parsed.category);
    const date = parsed.date ? formatGameDate(parsed.date) : null;
    const matchup = names
      ? `${names.a} vs ${names.b}`
      : `${versus.player1.playerCode} vs ${versus.player2.playerCode}`;
    const segments = [matchup, sport, date].filter((s): s is string => !!s);
    const competitor = names?.yes || parsed.outcome;
    return {
      marketLabel: segments.join(' — '),
      betTeam: competitor,
      // 1v1 labels already spell both competitors' full names, so the badge
      // token and the highlighted token are the same string.
      titleTeam: competitor,
    };
  }

  // World Cup winner futures (e.g. KXMENWORLDCUP-26-ES): a single-team "to win
  // the whole tournament" market. It carries no matchup/GameInfo, so without
  // this it falls through to the truncated raw ticker and the raw code becomes
  // the card title. Render a stable "World Cup Winner" label; the backed nation
  // is the bet team. The nation name rides on the live frame's subtitle
  // ("Spain") — same store read as deriveBetTeam above — and degrades to the
  // ticker's outcome code ("ES") before the frame loads, so it's never raw or
  // blank. Anchored startsWith so it can't swallow other unknown tickers (and
  // deliberately does NOT match the women's KXWOMENWORLDCUP series).
  if (!parsed.game && ticker.startsWith('KXMENWORLDCUP-')) {
    const outcomeCode = ticker.split('-').pop() ?? '';
    const mkt = useMarketStore.getState().getMarket(ticker);
    // Resolution chain, most to least readable. The edge feed omits `subtitle`
    // on advance-stamped legs (#98), which used to drop this straight to the
    // raw "ES" code: when the leg carries the final's canonical key, the
    // linked row already holds the nation's display name, matched on the
    // canonical `yesOutcome` the producer stamped.
    const nation =
      mkt?.subtitle?.trim() ||
      linkedOutcomeName(mkt) ||
      mkt?.yesOutcome?.trim() ||
      outcomeCode;
    // "World Cup Winner" names no competitor, so there is nothing to
    // highlight inside it.
    return { marketLabel: 'World Cup Winner', betTeam: nation || null, titleTeam: null };
  }

  // #733: period lines and Game Props have no safe client-side ticker split,
  // but their live Hydra frame carries canonical matchup identity plus the
  // venue's exact family/outcome wording. This branch is intentionally before
  // the raw `!displayGame` fallback and intentionally excludes the three
  // established full-game families.
  const tickerSeries = (ticker.split('-')[0] || '').toUpperCase();
  const isExtendedCfbTicker = (tickerSeries.startsWith('KXNCAAF') &&
    tickerSeries !== 'KXNCAAFGAME' &&
    tickerSeries !== 'KXNCAAFSPREAD' &&
    tickerSeries !== 'KXNCAAFTOTAL')
    // NFL game props (nfl-game-lines-props) ride the same wire-based branch;
    // it returns null for the lines / props series and they fall through.
    || (tickerSeries.startsWith('KXNFL') && tickerSeries !== 'KXNFLGAME');
  const extendedCfb = isExtendedCfbTicker
    ? describeExtendedCfbMarket(ticker, useMarketStore.getState().getMarket(ticker))
    : null;
  if (extendedCfb) return extendedCfb;

  const displayGame = resolvedGamePair(ticker, parsed);

  if (!displayGame) {
    // #837 — the matchup is genuinely unknown, but the bet is not. Keep the
    // EXACT ticker and the Draw/Tie/BTTS meaning readable instead of degrading
    // to a truncated raw ticker with no side badge at all. Deliberately not
    // truncated: the full ticker is the only identity this row has left.
    const meaning = unresolvedBetMeaning(parsed);
    if (meaning) {
      const segments = [
        ticker,
        sportFromCategory(parsed.category),
        parsed.date ? formatGameDate(parsed.date) : null,
      ].filter((seg): seg is string => !!seg);
      return {
        marketLabel: segments.join(' — '),
        // Mirrors the period prefix the resolved path applies below, so a 1H
        // BTTS leg still reads "1H - Both Score".
        betTeam: parsed.period ? `${parsed.period} - ${meaning}` : meaning,
        // Nothing in the label names a single competitor to highlight.
        titleTeam: null,
      };
    }
    if (!_loggedFallback.has(ticker)) {
      _loggedFallback.add(ticker);
      // eslint-disable-next-line no-console
      console.warn('[describeMarket] no game parsed — falling back to raw ticker', {
        ticker,
        category: parsed.category,
        series: parsed.series,
        outcome: parsed.outcome,
        date: parsed.date,
      });
    }
    return { marketLabel: truncateTicker(ticker), betTeam: null, titleTeam: null };
  }
  const { awayCode, homeCode } = displayGame;
  if (parsed.category === 'esports-game' && unresolvedBetMeaning(parsed) === 'Tie') {
    const segments = [
      `${awayCode} vs ${homeCode}`,
      sportFromCategory(parsed.category),
      parsed.date ? formatGameDate(parsed.date) : null,
    ].filter((s): s is string => !!s);
    return { marketLabel: segments.join(' — '), betTeam: 'Tie', titleTeam: null };
  }
  // The team in the parenthetical prefix is the market's named team (the YES
  // team — the trailing -X in the ticker), independent of the user's side.
  // The YES/NO badge already conveys direction; pairing it with the implied
  // backed team confused readers because a NO on "BAL market" reads more
  // naturally as "(BAL) NO" than as "(NYY) NO".
  //
  // For spread/total tickers the raw outcome (`STL3` or `13`) is the wrong
  // thing to show; we look up the hydra title for the canonical line
  // (`COL2` → "Colorado wins by over 2.5 goals" → `COL-2.5`) and fall back
  // to bucket math only when the market frame isn't in the store yet.
  // The side label. For NBA period bets, prefix the period so the button reads
  // e.g. "1Q - TOT 39.5" / "1H - SAS-2.5" / "1H - NYK" — the game label stays
  // the canonical "AWAY @ HOME — NBA — MM-DD" so the row rolls up under the game.
  const betTeamBase = deriveBetTeam(ticker, parsed.category, parsed.outcome, homeCode);
  // Knockout "To Advance" reuses the soccer-game category; the series marker
  // distinguishes it so the bet team reads "ENG Adv" — a held advance position
  // settles on advancement (incl. ET + pens), not the regulation 1X2 winner.
  const isAdvance = parsed.category === 'soccer-game' && parsed.series === 'KXSOCCERADVANCE';
  const soccerBetTeamBase =
    parsed.category === 'soccer-game' && parsed.outcome && isSoccerDraw(parsed.outcome)
      ? 'Draw'
      : parsed.outcome === 'BTTS'
        ? 'Both Score'
        : isAdvance && parsed.outcome
          ? `${parsed.outcome} Adv`
          : betTeamBase;
  // MLB player props / event markets: the raw outcome alone ("2+", "SD2",
  // "EXTRAS") doesn't identify the bet — fold in the player / team / market.
  // (parsed.player is only set on the eight mlb-* prop categories.)
  // NFL player props (nfl-player-props): "B. Purdy #13 350+ Passing Yards" on
  // a ladder rung (#964: the stat is named), "S. Darnold #14 1st Touchdown" on
  // a touchdown binary, "No Touchdown" on the NONE leg — the parser's own
  // wording authority.
  const nflParts = nflPropBetParts(parsed);
  const nflProp = nflPropBetLabel(parsed);
  const finalBetTeamBase =
    nflProp !== null
      ? nflProp
      : parsed.category.startsWith('mlb-') && parsed.player && parsed.outcome
      ? `${parsed.player.displayName ?? parsed.player.playerId} ${parsed.outcome}`
      : parsed.category === 'mlb-teamtotal' && parsed.outcome
        ? parsed.outcome.replace(/^([A-Z]+)(\d+)$/, '$1 $2+')
        : parsed.category === 'mlb-extras'
          ? 'Extras'
          : soccerBetTeamBase;
  const betTeam = parsed.period ? `${parsed.period} - ${finalBetTeamBase}` : finalBetTeamBase;
  const sport = sportFromCategory(parsed.category);

  // Playoff series-winner: "VGK vs ANA — NHL R2". No date — the series
  // spans days. Round comes from playoffSeries.round.
  if (parsed.playoffSeries) {
    const sportLabel = sport ? `${sport} R${parsed.playoffSeries.round}` : `R${parsed.playoffSeries.round}`;
    const segments = [`${awayCode} vs ${homeCode}`, sportLabel].filter(
      (s): s is string => !!s,
    );
    return { marketLabel: segments.join(' — '), betTeam, titleTeam: betTeam };
  }

  const date = parsed.date ? formatGameDate(parsed.date) : null;
  // Doubleheader game number keeps the two same-day games distinguishable
  // ("PIT @ NYY (G1) — MLB — 07-22" vs the G2 nightcap).
  const dhLabel = displayGame.gameNumber ? ` (G${displayGame.gameNumber})` : '';

  // #685, canonical-team-names — every sport spells the matchup with the
  // names hydra stamps (`canonicalTeamNames`), so a card reads "New York City
  // @ Toronto" like the dashboard row for the same fixture instead of "NYC @
  // TOR". One O(1) store read (see this file's MUST-STAY-O(1) note); a game
  // hydra has not named keeps its codes.
  //
  // Orientation comes from the index, i.e. from hydra's canonical away/home —
  // the same authority the dashboard row and the deep link use — rather than
  // from the ticker's packed pair.
  //
  // Two lookups, because a frame can carry the key before the away/home pair.
  // `getGameMatchup` carries hydra's canonical away/home and is preferred — it
  // makes the card agree with the row and the deep link. Without it the
  // per-code reader names both sides against THIS ticker's parsed codes.
  const store = useMarketStore.getState();
  const named = store.getGameMatchup(ticker);
  const awayLabel = named?.awayName ?? store.getTeamName(ticker, awayCode) ?? awayCode;
  const homeLabel = named?.homeName ?? store.getTeamName(ticker, homeCode) ?? homeCode;

  // The highlight token has to be the string that actually appears in the
  // label. `betTeam` is the code (it doubles as the narrow "(NYC) YES" badge),
  // so for a named matchup we hand back the club name instead — and null when
  // the bet names no single club in the title (a draw, BTTS, or a line bet,
  // none of which were ever highlighted).
  // KNOWN DIVERGENCE from the unresolved branch above, deliberately left as-is
  // (#837): for a draw / BTTS / line bet this hands back `betTeam` ("Draw",
  // "Both Score") as the highlight token even though no such token appears in
  // the label, which the interface comment at the top of this file says should
  // be null. It is inert rather than wrong — `findTeamToken` returns null when
  // the token isn't in the title and `BetTeamTitle` then renders the plain
  // title, so the DOM is byte-identical to passing null. Pre-dates #837 (the
  // #685 test pins the same value); fixing it is a one-line contract change
  // that belongs in its own PR, not in a parser fix.
  const isNamed = awayLabel !== awayCode || homeLabel !== homeCode;
  const betCode = parsed.outcome && !isSoccerDraw(parsed.outcome)
    ? normalizeTeamCode(parsed.outcome)
    : null;
  const titleTeam = isNamed
    ? (betCode === (named?.awayCode ?? awayCode) ? awayLabel
      : betCode === (named?.homeCode ?? homeCode) ? homeLabel
        : null)
    : betTeam;

  const segments = [`${awayLabel} @ ${homeLabel}${dhLabel}`, sport, date].filter(
    (s): s is string => !!s,
  );
  // #964: a ladder prop's badge carries the subject and the stat rides beside
  // or under it, so an ellipsized badge (#603) never hides what the rung counts.
  const propSplit = nflParts?.marketType && betTeam === `${nflParts.subject} ${nflParts.marketType}`
    ? { marketType: nflParts.marketType, badgeTeam: nflParts.subject, badgeThreshold: nflParts.threshold }
    : {};
  return { marketLabel: segments.join(' — '), betTeam, titleTeam, ...propSplit };
}

/** Trailing ticker outcome → the canonical code shape the index is keyed by.
 *  A soccer advance/moneyline outcome is already the bare code; upper-casing
 *  is all that separates the two spellings. */
function normalizeTeamCode(outcome: string): string {
  return outcome.trim().toUpperCase();
}

export function truncateTicker(ticker: string): string {
  if (ticker.length <= 24) return ticker;
  const parts = ticker.split('-');
  if (parts.length >= 2) return `...${parts.slice(-2).join('-')}`;
  return `...${ticker.slice(-20)}`;
}

function sportFromCategory(category: string): string | null {
  const prefix = category.split('-')[0];
  switch (prefix) {
    case 'nba':
      return 'NBA';
    case 'wnba':
      return 'WNBA';
    case 'nfl':
      return 'NFL';
    case 'nhl':
      return 'NHL';
    case 'mlb':
      return 'MLB';
    case 'ncaa':
      return 'NCAA';
    case 'tennis':
      return 'Tennis';
    case 'ufc':
      return 'UFC';
    case 'boxing':
      return 'Boxing';
    case 'esports':
      return 'Esports';
    case 'cricket':
      return 'Cricket';
    case 'cfb':
      return 'CFB';
    case 'soccer':
      return 'Soccer';
    default:
      return null;
  }
}

function formatGameDate(yyyyMmDd: string): string {
  const parts = yyyyMmDd.split('-');
  if (parts.length !== 3) return yyyyMmDd;
  return `${parts[1]}-${parts[2]}`;
}
